import React, { useMemo } from "react";
import styles from "./StreakCard.module.css";
import { toLocalDate, startOfWeek } from "../utils/date";

function ymdLocal(input){
  const dt = new Date(input);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export default function StreakCard({ checkins = [], weeklyGoal = 3, series = null }) {
  const { dayStreak, bestDayStreak, weekStreak } = useMemo(() => {
    const dayKeys = checkins
      .map((c) => toLocalDate(c.date || c.created_at || c.checkin_at || c.day || Date.now()))
      .map((d) => ymdLocal(d));
    const days = new Set(dayKeys);

    let dayStreak = 0;
    if (dayKeys.length) {
      const today = new Date();
      // Encontra o último dia com treino (dayKeys já está em formato YYYY-MM-DD local)
      const sortedDays = dayKeys.slice().sort().reverse();
      const lastDayStr = sortedDays[0];

      if (lastDayStr) {
        // Parse YYYY-MM-DD string to local Date
        const [y, m, d] = lastDayStr.split('-').map(Number);
        const lastDay = new Date(y, m - 1, d);

        // Calcula dias desde o último treino (em dias locais)
        const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const lastDate = new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate());
        const daysSinceLastTraining = Math.floor((todayDate - lastDate) / (1000 * 60 * 60 * 24));

        // Se o último treino foi hoje ou ontem: começar a contar
        // Se foi 2+ dias atrás: streak quebrou
        if (daysSinceLastTraining <= 1) {
          // Contar retroativamente a partir do último dia com treino
          let cursor = new Date(lastDate);
          for (;;) {
            const k = ymdLocal(cursor);
            if (days.has(k)) {
              dayStreak += 1;
              cursor.setDate(cursor.getDate() - 1);
            } else {
              break;
            }
          }
        }
      }
    }

    let bestDayStreak = 0;
    if (days.size) {
      let run = 0;
      const scanDays = 180;
      const start = dayKeys
        .map((s) => toLocalDate(s))
        .sort((a, b) => b - a)[0] || new Date();
      const ref = new Date(start);
      for (let i = 0; i < scanDays; i++) {
        const k = ymdLocal(ref);
        if (days.has(k)) run += 1; else { bestDayStreak = Math.max(bestDayStreak, run); run = 0; }
        ref.setDate(ref.getDate() - 1);
      }
      bestDayStreak = Math.max(bestDayStreak, run);
    }

    const byWeek = new Map();
    if (Array.isArray(series) && series.length) {
      for (const s of series) {
        const dt = toLocalDate(s.date);
        const wk = startOfWeek(dt);
        const key = ymdLocal(wk);
        byWeek.set(key, (byWeek.get(key) || 0) + Number(s.count || 0));
      }
    } else {
      for (const c of checkins) {
        const dt = toLocalDate(c.date || c.created_at || c.checkin_at || c.day || Date.now());
        const wk = startOfWeek(dt);
        const key = ymdLocal(wk);
        byWeek.set(key, (byWeek.get(key) || 0) + 1);
      }
    }

    let weekStreak = 0;
    if (byWeek.size) {
      const allWeeks = Array.from(byWeek.keys())
        .map((s) => toLocalDate(s))
        .sort((a, b) => b - a);

      let startWeek = null;
      for (const d of allWeeks) {
        const k = ymdLocal(d);
        const count = byWeek.get(k) || 0;
        if (count >= weeklyGoal) {
          startWeek = new Date(d);
          break;
        }
      }

      if (startWeek) {
        const cur = new Date(startWeek);
        for (let i = 0; i < 52; i++) {
          const k = ymdLocal(cur);
          const count = byWeek.get(k) || 0;
          if (count >= weeklyGoal) {
            weekStreak += 1;
            cur.setDate(cur.getDate() - 7);
          } else {
            break;
          }
        }
      }
    }

    return { dayStreak, bestDayStreak, weekStreak };
  }, [checkins, weeklyGoal, series]);

  const plural = (n, s, p) => `${n} ${n === 1 ? s : p}`;
  const fmtDays = (n) => plural(n, "dia", "dias");
  const fmtWeeks = (n) => plural(n, "semana", "semanas");

  const valClass = (n) => `${styles.value} ${n > 0 ? styles.valueGood : styles.valueNeutral}`;

  return (
    <div className={styles.card}>
      <div className={styles.row}>
        <div className={styles.item}>
          <div className={styles.kicker}>
            <span>Dias seguidos (treinando)</span>
            <span
              className={styles.info}
              role="img"
              aria-label="Número de dias consecutivos com ao menos um treino registrado."
              title="Número de dias consecutivos com ao menos um treino registrado."
            >
              ⓘ
            </span>
          </div>
          <div className={valClass(dayStreak)}>{fmtDays(dayStreak)}</div>
        </div>
        <div className={styles.item}>
          <div className={styles.kicker}>
            <span>Seu recorde de dias seguidos</span>
            <span
              className={styles.info}
              role="img"
              aria-label="Maior sequência de dias consecutivos com treino registrada."
              title="Maior sequência de dias consecutivos com treino registrada."
            >
              ⓘ
            </span>
          </div>
          <div className={valClass(bestDayStreak)}>{fmtDays(bestDayStreak)}</div>
        </div>
        <div className={styles.item}>
          <div className={styles.kicker}>
            <span>Semanas batendo a meta</span>
            <span
              className={styles.info}
              role="img"
              aria-label={`Semanas consecutivas com pelo menos ${weeklyGoal} treinos concluídos.`}
              title={`Semanas consecutivas com pelo menos ${weeklyGoal} treinos concluídos.`}
            >
              ⓘ
            </span>
          </div>
          <div className={valClass(weekStreak)}>{fmtWeeks(weekStreak)}</div>
        </div>
      </div>
      <div className={styles.hint}>
        Uma semana “na meta” é quando há {weeklyGoal}+ treinos.
      </div>
    </div>
  );
}
